import React, { useMemo } from 'react';
// @ts-ignore — resolved at build time via Vite aliases
import { usePage, useContentToken, EditableToken } from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { getPropertiesByState, getStateName } from './propertyUtils';
import { MarketPage, computeBounds } from './MarketPage';

function parseStateFromAlias(alias: string): string | null {
  const parts = alias?.split('--') ?? [];
  if (parts.length >= 2) return parts[1];
  return null;
}

function parseStateFromPath(path: string): string | null {
  const match = path?.match(/^\/apartments\/([^/]+)\/?$/);
  if (match) return match[1];
  return null;
}

export function StateListingPage() {
  const propertiesByState = getPropertiesByState();
  const page = usePage();
  const titlePrefix = useContentToken('statelistingpage.title_prefix');

  const stateSlug = useMemo(() => {
    const fromAlias = parseStateFromAlias(page?.alias || '');
    const fromPath = parseStateFromPath(page?.path || '');
    const parsed = fromAlias || fromPath;
    if (parsed) return parsed;

    const keys = Object.keys(propertiesByState);
    return keys.length > 0 ? keys[0] : '';
  }, [page, propertiesByState]);

  const stateData = propertiesByState[stateSlug] || null;
  const stateName = stateData?.name || getStateName(stateSlug.toUpperCase());

  const initialBounds = useMemo(() => {
    if (!stateData?.cities) return undefined;
    const stateProps = Object.values(stateData.cities).flatMap((city: any) => city.properties);
    return computeBounds(stateProps);
  }, [stateData]);

  const prefix = titlePrefix || 'Apartments in';

  return (
    <>
      {/* Hidden EditableToken wrapper so title prefix is inline-editable */}
      <span aria-hidden="true" style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', borderWidth: 0 }}>
        <EditableToken path="statelistingpage.title_prefix" type="text" label="Title Prefix" defaultValue={contentSchema.tokens['statelistingpage.title_prefix'].defaultValue} as="span">
          {prefix}
        </EditableToken>
      </span>
      <MarketPage title={`${prefix} ${stateName}`} initialBounds={initialBounds} />
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'StateListing',
  displayName: 'State Listing Page',
  tokens: {
    'statelistingpage.title_prefix': {
      label: 'Title Prefix',
      type: 'text',
      defaultValue: 'Apartments in',
    },
  },
};

(StateListingPage as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default StateListingPage;
